import {
	ANNOTATION_LOCATION,
	DEFAULT_NAMESPACE,
	Entity,
	GroupEntity,
	RELATION_MEMBER_OF,
	stringifyEntityRef,
	UserEntity,
} from '@backstage/catalog-model';
import {
	Avatar,
	InfoCard,
	InfoCardVariants,
	Link,
} from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import {
	catalogApiRef,
	EntityRefLinks,
	getEntityRelations,
	useEntity,
} from '@backstage/plugin-catalog-react';

import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Cached';
import EmailIcon from '@material-ui/icons/Email';
import GroupIcon from '@material-ui/icons/Group';
import { LinksGroup } from './Meta/LinksGroup';
import PersonIcon from '@material-ui/icons/Person';
import React, { useCallback, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef, ScaffolderScaffoldOptions } from '@backstage/plugin-scaffolder-react';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import Checkbox from '@material-ui/core/Checkbox';
import InputLabel from '@material-ui/core/InputLabel';
import { usePermission } from '@backstage/plugin-permission-react';
import { catalogEntityDeletePermission, catalogEntityReadPermission, catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';
import { makeStyles } from '@material-ui/core/styles';

const CardTitle = (props: { title?: string }) =>
	props.title ? (
		<Box display="flex" alignItems="center">
			<PersonIcon fontSize="inherit" />
			<Box ml={1}>{props.title}</Box>
		</Box>
	) : null;

export const EntityUserProfileCard = (props: {
	variant?: InfoCardVariants;
	showLinks?: boolean;
}) => {
	const { entity: user } = useEntity<UserEntity>();

  const useStyles = makeStyles((theme) => ({
    largeFont: {
      fontSize: theme.typography.pxToRem(18),
    },
    warningText: {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
    dangerText: {
      color: theme.palette.error.main,
    }
  }));

  const classes = useStyles();

	const alertApi = useApi(alertApiRef);
	const scaffolderApi = useApi(scaffolderApiRef);
	const catalogApi = useApi(catalogApiRef);

	// Declare default values to prevent errors in useEffect
	const displayName = user?.spec?.profile?.displayName ?? user?.metadata?.name ?? '';
	const email = user?.spec?.profile?.email ?? '';

	// Declare states at the top of the component
	const [open, setOpen] = useState(false);
	const [editedName, setEditedName] = useState(displayName);
	const [editedEmail, setEditedEmail] = useState(email);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const [selectedRole, setSelectedRole] = useState<GroupEntity | null>(null);
	const [availableRoles, setRoles] = useState<GroupEntity[]>([]);
	const [availableGroups, setAvailableGroups] = useState<GroupEntity[]>([]);
	const [selectedGroups, setSelectedGroups] = useState<GroupEntity[]>([]);

  // Use permissions
  const readPermission = usePermission({ permission: catalogEntityReadPermission, resourceRef: stringifyEntityRef(user) }).allowed
  const deletePermission = usePermission({ permission: catalogEntityDeletePermission, resourceRef: stringifyEntityRef(user) }).allowed;
  const refreshPermission = usePermission({ permission: catalogEntityRefreshPermission, resourceRef: stringifyEntityRef(user) }).allowed;
  const scaffolderPermission = usePermission({ permission: taskCreatePermission }).allowed;

  const disabled = !readPermission || !refreshPermission || !scaffolderPermission || !deletePermission;

	const fetchAvailableRoles = async (): Promise<GroupEntity[]> => {
		const roleEntities = await catalogApi.getEntities({
			filter: { kind: 'Group', 'spec.type': 'role' },
		});
		return roleEntities.items.filter((e): e is GroupEntity => Boolean(e));
	};

	const fetchAvailableGroups = async (): Promise<GroupEntity[]> => {
		const groupEntities = await catalogApi.getEntities({
			filter: [
				{ kind: 'Group', 'spec.type': 'team' },
				{ kind: 'Group', 'spec.type': 'business-unit' },
			],
		});
		return groupEntities.items.filter((e): e is GroupEntity => Boolean(e));
	};

	const loadUserGroups = (groups: GroupEntity[]) => {
		const memberOfGroups = getEntityRelations(user, RELATION_MEMBER_OF, { kind: 'Group' });
		const memberGroupRefs = memberOfGroups.map(ref => stringifyEntityRef(ref));

		const filteredGroupEntities = groups.filter(group =>
			memberGroupRefs.includes(stringifyEntityRef(group)) &&
			(group.spec?.type === 'team' || group.spec?.type === 'business-unit')
		);

		setSelectedGroups(filteredGroupEntities);
	};

	const loadUserRoles = (roles: GroupEntity[]) => {
		const memberOfRoles = getEntityRelations(user, RELATION_MEMBER_OF, { kind: 'Group' });
		const memberRoleRefs = memberOfRoles.map(ref => stringifyEntityRef(ref));

		const roleEntity = roles.find(role =>
			memberRoleRefs.includes(stringifyEntityRef(role)) &&
			role.spec?.type === 'role'
		);

		setSelectedRole(roleEntity ?? null);
	};

	const loadModalData = async () => {
		const groups = await fetchAvailableGroups();
		const roles = await fetchAvailableRoles();

		setEditedName(displayName);
		setEditedEmail(email);
		setAvailableGroups(groups);
		setRoles(roles);
		loadUserGroups(groups);
		loadUserRoles(roles);
	};

	const refreshEntity = useCallback(async () => {
		await catalogApi.refreshEntity(stringifyEntityRef(user));
		alertApi.post({
			message: 'Refresh scheduled. Changes will be reflected shortly.',
			severity: 'info',
			display: 'transient',
		});
	}, [catalogApi, alertApi, user]);

	const {
		metadata: { description, links },
		spec: { profile },
	} = user;

	const handleEditClose = () => setOpen(false);
	const handleSave = async () => {

		const payload: ScaffolderScaffoldOptions = {
			templateRef: 'default/user-management',
			values: {
				fullName: editedName,
				email: editedEmail,
				role: stringifyEntityRef(selectedRole as Entity),
				groups: selectedGroups.map(group => stringifyEntityRef(group)),
			},
		};

		const response = await scaffolderApi.scaffold(payload);

		alertApi.post({
			message: 'Updating user profile...',
			severity: 'info',
		});

		setOpen(false);
		refreshEntity();
		window.location.href = `/create/tasks/${response.taskId}`;
	};
	const handleEditOpen = async () => {
		await loadModalData();
		setOpen(true);
	};
	const handleDeleteOpen = () => setDeleteOpen(true);
	const handleDeleteClose = () => setDeleteOpen(false);
	const handleDeleteConfirm = async () => {
		const locationRef = user.metadata.annotations?.[ANNOTATION_LOCATION];

		const payload = {
			templateRef: `${DEFAULT_NAMESPACE}/entity-delete`,
			values: { locationRef },
		} as ScaffolderScaffoldOptions;

		const response = await scaffolderApi.scaffold(payload);
		refreshEntity();
		setDeleteOpen(false);
		window.location.href = `/create/tasks/${response.taskId}`;
	};

	return (
		<InfoCard
			title={<CardTitle title={displayName} />}
			subheader={description}
			variant={props.variant}
			action={
				<>
					<IconButton aria-label="refresh" onClick={refreshEntity} disabled={!refreshPermission}>
						<RefreshIcon />
					</IconButton>
					<IconButton aria-label="Edit" title="Edit Profile" onClick={handleEditOpen} disabled={disabled}>
						<EditIcon />
					</IconButton>
					<IconButton aria-label="Delete" title="Delete User" onClick={handleDeleteOpen} disabled={disabled}>
						<DeleteIcon />
					</IconButton>
				</>
			}
		>
			<Grid container spacing={3} alignItems="flex-start">
				<Grid item xs={12} sm={2} xl={1}>
					<Avatar displayName={displayName} picture={profile?.picture} />
				</Grid>
				<Grid item md={10} xl={11}>
					<List>
						{profile?.email && (
							<ListItem>
								<ListItemIcon>
									<Tooltip title="Email">
										<EmailIcon />
									</Tooltip>
								</ListItemIcon>
								<ListItemText>
									<Link to={`mailto:${profile.email}`}>{profile.email}</Link>
								</ListItemText>
							</ListItem>
						)}
						<ListItem>
							<ListItemIcon>
								<Tooltip title="Member of">
									<GroupIcon />
								</Tooltip>
							</ListItemIcon>
							<ListItemText>
								<EntityRefLinks
									entityRefs={getEntityRelations(user, RELATION_MEMBER_OF, { kind: 'Group' })}
									defaultKind="Group"
								/>
							</ListItemText>
						</ListItem>
						{props?.showLinks && <LinksGroup links={links} />}
					</List>
				</Grid>
			</Grid>
			<Dialog open={open} onClose={handleEditClose} fullWidth maxWidth="sm">
				<DialogTitle>Edit User Profile</DialogTitle>
				<DialogContent>
					{/* Display Name */}
					<TextField
						fullWidth
						label="Display Name"
						value={editedName}
						onChange={(e) => setEditedName(e.target.value)}
						margin="dense"
					/>

					{/* Email */}
					<TextField
						fullWidth
						label="Email"
						value={editedEmail}
						onChange={(e) => setEditedEmail(e.target.value)}
						margin="dense"
					/>

					{/* Role */}
					<FormControl fullWidth margin="dense">
						<InputLabel id="role-label">Role</InputLabel>
						<Select
							labelId="role-label"
							value={selectedRole ? JSON.stringify(selectedRole) : ''}
							onChange={(event, child) => {
								const selected = JSON.parse(event.target.value as string) as GroupEntity;
								setSelectedRole(selected);
							}}
						>
							{availableRoles.map(role => (
								<MenuItem key={role.metadata.name} value={JSON.stringify(role)}>
									{role.spec?.profile?.displayName ?? role.metadata.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{/* Groups */}
					<FormControl fullWidth margin="dense">
						<InputLabel id="groups-label">Groups</InputLabel>
						<Select
							labelId="groups-label"
							multiple
							value={selectedGroups.map(group => JSON.stringify(group))}
							onChange={(event, child) => {
								const selected = (event.target.value as string[]).map(val => JSON.parse(val) as GroupEntity);
								setSelectedGroups(selected);
							}}
							renderValue={(selected) => {
								const selectedEntities = (selected as string[]).map(val => JSON.parse(val) as GroupEntity);
								return selectedEntities
									.map(group => group.spec?.profile?.displayName ?? group.metadata.name)
									.join(', ');
							}}
						>
							{availableGroups.map(group => {
								const stringified = JSON.stringify(group);
								return (
									<MenuItem key={group.metadata.name} value={stringified}>
										<Checkbox
											checked={selectedGroups.some(g => g.metadata.name === group.metadata.name)}
										/>
										<ListItemText
											primary={group.spec?.profile?.displayName ?? group.metadata.name}
										/>
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleEditClose}>Cancel</Button>
					<Button onClick={handleSave} color="primary">Save</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={deleteOpen} onClose={handleDeleteClose} fullWidth maxWidth="sm">
				<DialogTitle>Confirm Deletion</DialogTitle>
				<DialogContent className={classes.largeFont}>
          Are you sure you want to <span className={classes.dangerText}>delete</span> this user?
          <br />
          <span className={classes.warningText}>Attention!</span>
          <br />
          The user will be removed from all groups and teams in which they are registered.
          <br />
          <span className={classes.dangerText}>They will no longer have access to the Developer Portal.</span>
          <br />
          If they wish to return, they will need to be re-registered by another user.
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteClose}>Cancel</Button>
					<Button onClick={handleDeleteConfirm} color="secondary">Delete</Button>
				</DialogActions>
			</Dialog>
		</InfoCard>
	);
};
